/**
 * AiAssistPanel — collapsible card inside the LeadDetailDrawer.
 *
 * Three actions: Summary, Intent Score, Drafted follow-up (email + SMS).
 * Cached results are persisted on the lead; click "Refresh" to recompute.
 */
import { useEffect, useState } from "react";
import { Sparkles, Loader2, RefreshCw, Copy, Mail, MessageSquareText, BarChart3, Send, Check } from "lucide-react";
import { api } from "../lib/api";

const SENTIMENT_COLOR = {
  positive: "text-emerald-700 bg-emerald-50 border-emerald-200",
  neutral: "text-slate-700 bg-slate-50 border-slate-200",
  negative: "text-red-700 bg-red-50 border-red-200",
};
const PRIO_COLOR = {
  high: "bg-red-500 text-white",
  medium: "bg-amber-400 text-[#3a2a00]",
  low: "bg-slate-300 text-[#1f2937]",
};

export default function AiAssistPanel({ lead, leadId, onRefresh }) {
  const [summary, setSummary] = useState(lead?.ai_summary || null);
  const [summaryAt, setSummaryAt] = useState(lead?.ai_summary_at || null);
  const [score, setScore] = useState(lead?.ai_score_payload || null);
  const [scoreAt, setScoreAt] = useState(lead?.ai_score_at || null);
  const [draft, setDraft] = useState(null);
  const [draftChannel, setDraftChannel] = useState("email");
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    // Only sync from the lead prop when we open a different lead.
    // Avoids overwriting the fresh score we just fetched in this drawer session.
    setSummary(lead?.ai_summary || null);
    setSummaryAt(lead?.ai_summary_at || null);
    setScore(lead?.ai_score_payload || null);
    setScoreAt(lead?.ai_score_at || null);
    setDraft(null);
    setErr("");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId]);

  const runSummary = async (force = false) => {
    setBusy("summary"); setErr("");
    try {
      const { data } = await api.post(`/dealer/lead/${leadId}/ai/summary`, { force });
      setSummary(data.summary); setSummaryAt(data.at);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "AI service unavailable.");
    } finally { setBusy(""); }
  };
  const runScore = async (force = false) => {
    setBusy("score"); setErr("");
    try {
      const { data } = await api.post(`/dealer/lead/${leadId}/ai/score`, { force });
      setScore(data.score); setScoreAt(data.at);
      onRefresh?.();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "AI service unavailable.");
    } finally { setBusy(""); }
  };
  const runDraft = async () => {
    setBusy("draft"); setErr(""); setDraft(null);
    try {
      const { data } = await api.post(`/dealer/lead/${leadId}/ai/draft-followup`, { channel: draftChannel, tone: "friendly-pro" });
      setDraft(data.draft);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "AI service unavailable.");
    } finally { setBusy(""); }
  };
  // Prefer freshly fetched state, but always fall back to whatever is cached
  // on the lead doc — fixes the case where the panel renders before useEffect
  // syncs from the freshly-loaded lead prop.
  const effectiveScore = score ?? lead?.ai_score_payload ?? null;
  const effectiveScoreAt = scoreAt ?? lead?.ai_score_at ?? null;
  const effectiveSummary = summary ?? lead?.ai_summary ?? null;
  const effectiveSummaryAt = summaryAt ?? lead?.ai_summary_at ?? null;

  const sendDraftEmail = async () => {
    if (!draft) return;
    if (!window.confirm("Send this email to " + (lead?.email || "the lead") + " through Scott's address?")) return;
    setBusy("send"); setErr("");
    try {
      await api.post(`/dealer/lead/${leadId}/send-email`, {
        subject: draft.subject || "Following up",
        body: draft.body || "",
      });
      onRefresh?.();
      setDraft({ ...draft, _sent: true });
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Couldn't send via Resend.");
    } finally { setBusy(""); }
  };

  const copyToClipboard = async (text) => {
    try { await navigator.clipboard.writeText(text); } catch { /* noop */ }
  };

  return (
    <section className="p-4 border-b border-[#D9E1EA] bg-gradient-to-br from-[#F0F6FF] via-white to-white" data-testid="ai-assist-panel">
      <div className="text-xs font-black uppercase tracking-wider text-[var(--ssc-blue)] flex items-center gap-1.5 mb-2">
        <Sparkles size={13} /> AI Assist
      </div>

      {/* Score card */}
      <div className="rounded-xl border border-[#D9E1EA] bg-white p-3 mb-2" data-testid="ai-score-card">
        <div className="flex items-center justify-between">
          <div className="text-[10px] font-extrabold uppercase tracking-widest text-[#6b7c93] inline-flex items-center gap-1.5">
            <BarChart3 size={11} /> Buyer Intent
          </div>
          <button onClick={() => runScore(true)} disabled={busy === "score"} className="text-[10px] text-[var(--ssc-blue)] hover:underline inline-flex items-center gap-1 font-bold" data-testid="ai-score-refresh">
            {busy === "score" ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
            {effectiveScore ? "Refresh" : "Run score"}
          </button>
        </div>
        {effectiveScore ? (
          <div className="mt-2 flex items-start gap-3">
            <div className="shrink-0 w-14 h-14 rounded-xl bg-[var(--ssc-navy)] text-white flex flex-col items-center justify-center" data-testid="ai-score-value">
              <div className="text-[8px] font-bold uppercase tracking-widest opacity-70">Score</div>
              <div className="text-2xl font-black leading-none">{effectiveScore.intent_score}</div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className={`text-[10px] font-extrabold uppercase px-1.5 py-0.5 rounded ${SENTIMENT_COLOR[effectiveScore.sentiment] || SENTIMENT_COLOR.neutral} border`} data-testid="ai-sentiment">
                  {effectiveScore.sentiment}
                </span>
                <span className={`text-[10px] font-extrabold uppercase px-1.5 py-0.5 rounded ${PRIO_COLOR[effectiveScore.recommended_priority] || PRIO_COLOR.medium}`} data-testid="ai-priority">
                  {effectiveScore.recommended_priority} priority
                </span>
              </div>
              {effectiveScore.top_signal && (
                <div className="text-xs text-[var(--ssc-navy)] mt-1.5 font-semibold" data-testid="ai-signal">
                  Signal: {effectiveScore.top_signal}
                </div>
              )}
              {effectiveScoreAt && <div className="text-[10px] text-[#6b7c93] mt-1">Updated {new Date(effectiveScoreAt).toLocaleString()}</div>}
            </div>
          </div>
        ) : (
          <div className="mt-1 text-xs text-[#6b7c93]">Click "Run score" for an AI-generated intent score, sentiment, and priority recommendation.</div>
        )}
      </div>

      {/* Summary card */}
      <div className="rounded-xl border border-[#D9E1EA] bg-white p-3 mb-2" data-testid="ai-summary-card">
        <div className="flex items-center justify-between">
          <div className="text-[10px] font-extrabold uppercase tracking-widest text-[#6b7c93]">Where this stands</div>
          <button onClick={() => runSummary(true)} disabled={busy === "summary"} className="text-[10px] text-[var(--ssc-blue)] hover:underline inline-flex items-center gap-1 font-bold" data-testid="ai-summary-refresh">
            {busy === "summary" ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
            {effectiveSummary ? "Refresh" : "Run summary"}
          </button>
        </div>
        {effectiveSummary ? (
          <div className="mt-1.5 text-sm text-[var(--ssc-navy)] leading-snug whitespace-pre-wrap" data-testid="ai-summary-text">{effectiveSummary}</div>
        ) : (
          <div className="mt-1 text-xs text-[#6b7c93]">A 3-sentence "where this stands today + best next move" summary.</div>
        )}
        {effectiveSummaryAt && effectiveSummary && <div className="text-[10px] text-[#6b7c93] mt-1">Updated {new Date(effectiveSummaryAt).toLocaleString()}</div>}
      </div>

      {/* Draft follow-up */}
      <div className="rounded-xl border border-[#D9E1EA] bg-white p-3" data-testid="ai-draft-card">
        <div className="flex items-center justify-between">
          <div className="text-[10px] font-extrabold uppercase tracking-widest text-[#6b7c93]">Drafted Follow-up</div>
          <div className="flex items-center gap-1.5">
            <button onClick={() => setDraftChannel("email")} className={`text-[10px] font-bold inline-flex items-center gap-1 px-2 py-0.5 rounded ${draftChannel === "email" ? "bg-[var(--ssc-blue)] text-white" : "bg-[#F1F5F9] text-[#6b7c93]"}`} data-testid="ai-draft-mode-email">
              <Mail size={10} /> Email
            </button>
            <button onClick={() => setDraftChannel("sms")} className={`text-[10px] font-bold inline-flex items-center gap-1 px-2 py-0.5 rounded ${draftChannel === "sms" ? "bg-[var(--ssc-blue)] text-white" : "bg-[#F1F5F9] text-[#6b7c93]"}`} data-testid="ai-draft-mode-sms">
              <MessageSquareText size={10} /> SMS
            </button>
            <button onClick={runDraft} disabled={busy === "draft"} className="text-[10px] text-[var(--ssc-blue)] hover:underline inline-flex items-center gap-1 font-bold ml-1" data-testid="ai-draft-run">
              {busy === "draft" ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />} Draft
            </button>
          </div>
        </div>
        {draft ? (
          <div className="mt-2 space-y-1.5" data-testid="ai-draft-text">
            {draft.subject && draftChannel === "email" && (
              <div className="text-xs"><span className="font-bold text-[#6b7c93]">Subject:</span> <span className="text-[var(--ssc-navy)] font-bold">{draft.subject}</span></div>
            )}
            <div className="text-sm text-[var(--ssc-navy)] bg-[var(--ssc-paper)] rounded-lg p-2 whitespace-pre-wrap leading-snug">{draft.body}</div>
            <div className="flex gap-1.5 items-center flex-wrap">
              {draft._sent ? (
                <span className="text-[10px] text-emerald-600 font-extrabold inline-flex items-center gap-1" data-testid="ai-draft-sent">
                  <Check size={11} /> Sent through Scott's email
                </span>
              ) : (draftChannel === "email" && lead?.email) && (
                <button onClick={sendDraftEmail} disabled={busy === "send"} className="text-[10px] text-white bg-emerald-600 hover:bg-emerald-700 font-extrabold inline-flex items-center gap-1 px-2 py-1 rounded-full" data-testid="ai-draft-send">
                  {busy === "send" ? <Loader2 size={10} className="animate-spin" /> : <Send size={10} />} Send now
                </button>
              )}
              <button onClick={() => copyToClipboard(draftChannel === "email" ? `Subject: ${draft.subject}\n\n${draft.body}` : draft.body)} className="text-[10px] text-[var(--ssc-blue)] hover:underline inline-flex items-center gap-1 font-bold" data-testid="ai-draft-copy">
                <Copy size={10} /> Copy
              </button>
              {lead?.phone && draftChannel === "sms" && (
                <a href={`sms:${lead.phone}?body=${encodeURIComponent(draft.body)}`}
                   className="text-[10px] text-[var(--ssc-blue)] hover:underline inline-flex items-center gap-1 font-bold" data-testid="ai-draft-sms-link">
                  <MessageSquareText size={10} /> Open in texts
                </a>
              )}
            </div>
          </div>
        ) : (
          <div className="mt-1 text-xs text-[#6b7c93]">AI drafts a short personalized {draftChannel === "email" ? "email" : "text"} mentioning their specific interest and a clear next step.</div>
        )}
      </div>

      {err && <div className="text-xs text-red-600 mt-2" data-testid="ai-error">{err}</div>}
    </section>
  );
}
